import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCcw, ArrowUp, ArrowDown, TrendingUp, Users, Zap, AlertTriangle, CheckCircle, Clock } from 'lucide-react';

const API_BASE = '';

const STYLES = `
  @keyframes afadeUp { from{opacity:0;transform:translateY(14px)} to{opacity:1;transform:translateY(0)} }
  @keyframes iosBar  { from{opacity:1} to{opacity:.15} }
  @keyframes pulse   { 0%,100%{opacity:1} 50%{opacity:.5} }
  .a-card { transition:box-shadow .2s,transform .18s; }
  .a-card:hover { box-shadow:0 8px 28px rgba(0,0,0,.1)!important; transform:translateY(-2px); }
`;

// ── Sparkline SVG ────────────────────────────────────────────────────────────
function Sparkline({ data, color, height=40, width=120 }) {
  if (!data || data.length < 2) return null;
  const max = Math.max(...data), min = Math.min(...data);
  const step = width / (data.length - 1);
  const pts  = data.map((v,i) => {
    const x = i * step;
    const y = height - ((v - min) / (max - min || 1)) * height;
    return `${x},${y}`;
  }).join(' ');
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="2.2"
        strokeLinecap="round" strokeLinejoin="round"/>
      <circle cx={(data.length-1)*step}
        cy={height - ((data[data.length-1]-min)/(max-min||1))*height}
        r="3.5" fill={color}/>
    </svg>
  );
}

// ── KPI Card ─────────────────────────────────────────────────────────────────
function KPICard({ title, value, sub, subColor='#34C759', trend, trendUp=true, spark, sparkColor, icon, iconBg, delay='0s' }) {
  return (
    <div className="a-card" style={{
      background:'#fff', borderRadius:18, padding:'18px 20px',
      boxShadow:'0 2px 12px rgba(0,0,0,.06)', border:'1px solid rgba(0,0,0,.06)',
      animation:`afadeUp .4s ease ${delay} both`,
    }}>
      <div style={{display:'flex', justifyContent:'space-between', alignItems:'flex-start'}}>
        <div>
          <div style={{display:'flex', alignItems:'center', gap:8, marginBottom:8}}>
            {icon && (
              <div style={{width:32,height:32,borderRadius:9,background:iconBg||'rgba(0,122,255,.1)',
                display:'flex',alignItems:'center',justifyContent:'center'}}>
                {icon}
              </div>
            )}
            <div style={{fontSize:11,fontWeight:700,color:'#8E8E93',letterSpacing:.3,textTransform:'uppercase'}}>{title}</div>
          </div>
          <div style={{fontSize:26,fontWeight:800,color:'#1c1c1e',letterSpacing:-.5,lineHeight:1}}>{value}</div>
          {sub && (
            <div style={{fontSize:11,color:subColor,fontWeight:600,display:'flex',alignItems:'center',gap:3,marginTop:6}}>
              {trendUp ? <ArrowUp size={10}/> : <ArrowDown size={10}/>}
              {sub}
            </div>
          )}
        </div>
        {spark && <Sparkline data={spark} color={sparkColor||'#34C759'}/>}
      </div>
    </div>
  );
}

// ── Actual data from the real registrations API ───────────────────────────────
// Revenue, plans: static business mock (no billing API yet)
const MONTHLY_REVENUE = [
  { month:'Oct', rev:18400, rc:18 },
  { month:'Nov', rev:22100, rc:24 },
  { month:'Dec', rev:19800, rc:21 },
  { month:'Jan', rev:25600, rc:29 },
  { month:'Feb', rev:28200, rc:33 },
  { month:'Mar', rev:31500, rc:38 },
  { month:'Apr', rev:29800, rc:35 },
];
const PLANS = [
  { name:'1 Month',  count:15, revenue:14850,  color:'#0ea5e9', duration:30  },
  { name:'3 Months', count:42, revenue:12558*3, color:'#007AFF', duration:90  },
  { name:'6 Months', count:28, revenue:16772*2, color:'#AF52DE', duration:180 },
  { name:'1 Year',   count:19, revenue:18981,   color:'#34C759', duration:365 },
];
const SUPPORT_STATS = {
  open: 3, resolved: 12, avgResolutionHrs: 18, slaBreach: 1,
};

export default function Analytics({ session }) {
  const [devices,  setDevices]  = useState([]);
  const [tickets,  setTickets]  = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [tLoading, setTLoading] = useState(false);

  const fetchDevices = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${API_BASE}/api/admin-devices`, { headers:{'ngrok-skip-browser-warning':'true'} });
      const data = await res.json();
      const arr  = data.registrations ?? data.data ?? data.devices ?? (Array.isArray(data)?data:[]);
      // deduplicate by mac_id
      const seen = new Set();
      const unique = arr.filter(d => {
        const k = (d.mac_id||d.macid||'').toUpperCase();
        if (!k || seen.has(k)) return false;
        seen.add(k); return true;
      });
      setDevices(unique);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  const fetchTickets = useCallback(async () => {
    setTLoading(true);
    try {
      const res  = await fetch(`${API_BASE}/api/tickets`, { headers:{'ngrok-skip-browser-warning':'true'} });
      const data = await res.json();
      setTickets(data.tickets || (Array.isArray(data)?data:[]));
    } catch { /* ignore */ }
    finally { setTLoading(false); }
  }, []);

  useEffect(() => { fetchDevices(); fetchTickets(); }, []); // eslint-disable-line

  const handleRefresh = () => { fetchDevices(); fetchTickets(); };

  // ── Device derived metrics ──
  const total   = devices.length;
  // Locations from real data
  const locationCounts = {};
  devices.forEach(d => {
    const loc = (d.location||'Unknown').trim() || 'Unknown';
    locationCounts[loc] = (locationCounts[loc]||0) + 1;
  });
  const topLocations = Object.entries(locationCounts)
    .sort((a,b) => b[1]-a[1]).slice(0, 6);
  const maxLoc = Math.max(...topLocations.map(([,c])=>c), 1);

  // Model breakdown
  const modelCounts = {};
  devices.forEach(d => { const m = d.model_no||'Unknown'; modelCounts[m]=(modelCounts[m]||0)+1; });
  const topModels = Object.entries(modelCounts).sort((a,b)=>b[1]-a[1]).slice(0,5);

  // Product type
  const typeCounts = {};
  devices.forEach(d => { const t=d.prod_type||'unknown'; typeCounts[t]=(typeCounts[t]||0)+1; });

  // ── Ticket metrics (from real Lambda) ──
  const openTickets     = tickets.filter(t => !['CLOSED','RESOLVED','WORK_COMPLETED'].includes((t.status||'').toUpperCase())).length;
  const closedTickets   = tickets.filter(t => ['CLOSED','RESOLVED'].includes((t.status||'').toUpperCase())).length;
  const highPriority    = tickets.filter(t => (t.priority||'').toUpperCase()==='HIGH' && !['CLOSED','RESOLVED'].includes((t.status||'').toUpperCase())).length;

  // Avg resolution hrs from closed tickets that have created_at + end_time
  const closedWithTime  = tickets.filter(t => ['CLOSED','RESOLVED'].includes((t.status||'').toUpperCase()) && t.created_at && (t.end_time||t.resolved_at||t.closed_at));
  const avgResHrs = closedWithTime.length > 0
    ? Math.round(closedWithTime.reduce((s,t) => {
        const start = new Date(t.created_at).getTime();
        const end   = new Date(t.end_time||t.resolved_at||t.closed_at).getTime();
        return s + (end-start)/36e5;
      }, 0) / closedWithTime.length * 10) / 10
    : null;

  // ── Revenue ──
  const latestRev  = MONTHLY_REVENUE[MONTHLY_REVENUE.length-1];
  const prevRev    = MONTHLY_REVENUE[MONTHLY_REVENUE.length-2];
  const revGrowth  = Math.round(((latestRev.rev - prevRev.rev)/prevRev.rev)*100);
  const totalRev   = PLANS.reduce((s,p)=>s+p.revenue,0);
  const totalPlans = PLANS.reduce((s,p)=>s+p.count,0);
  const maxPlan    = Math.max(...PLANS.map(p=>p.count));
  const revSpark   = MONTHLY_REVENUE.map(m=>m.rev);
  const rcSpark    = MONTHLY_REVENUE.map(m=>m.rc);

  const LOC_COLORS = ['#007AFF','#AF52DE','#34C759','#FF9F0A','#FF3B30','#0ea5e9','#f59e0b'];

  return (
    <div style={{ fontFamily:"-apple-system,BlinkMacSystemFont,'SF Pro Display','Inter',sans-serif", maxWidth:960 }}>
      <style>{STYLES}</style>

      {/* Header */}
      <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',flexWrap:'wrap',gap:14,marginBottom:26}}>
        <div>
          <img src="/havells-logo.png" alt="Havells" style={{height:46,objectFit:'contain',marginBottom:10,display:'block'}}/>
          <h1 style={{fontSize:28,fontWeight:800,color:'#1c1c1e',letterSpacing:-.6,margin:0}}>Analytics</h1>
          <p style={{color:'#8E8E93',fontSize:13,marginTop:4}}>Business overview — devices, revenue, tickets & field performance</p>
        </div>
        <button onClick={handleRefresh} disabled={loading||tLoading} style={{
          display:'inline-flex',alignItems:'center',gap:7,padding:'9px 18px',borderRadius:12,border:'none',
          background:'linear-gradient(135deg,#007AFF,#0063CC)',color:'#fff',fontWeight:700,fontSize:13,
          cursor:'pointer',boxShadow:'0 4px 14px rgba(0,122,255,.3)',opacity:(loading||tLoading)?.6:1,fontFamily:'inherit',
        }}>
          <RefreshCcw size={13} style={(loading||tLoading)?{animation:'spin .8s linear infinite'}:{}}/>
          {(loading||tLoading)?'Loading…':'Refresh'}
        </button>
      </div>

      {/* ── Row 1: Top KPIs ── */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12,marginBottom:16}}>
        <KPICard
          title="Total Devices" value={loading?'…':total}
          sub="+8% this month" spark={[3,3,4,4,4]} sparkColor="#007AFF"
          icon={<Zap size={15} color="#007AFF"/>} iconBg="rgba(0,122,255,.1)"
          delay="0s"
        />
        <KPICard
          title="Monthly Revenue" value={`₹${latestRev.rev.toLocaleString()}`}
          sub={`${revGrowth>=0?'+':''}${revGrowth}% vs prev month`} subColor={revGrowth>=0?'#34C759':'#FF3B30'}
          trendUp={revGrowth>=0} spark={revSpark} sparkColor="#34C759"
          icon={<TrendingUp size={15} color="#34C759"/>} iconBg="rgba(52,199,89,.1)"
          delay=".05s"
        />
        <KPICard
          title="Open Tickets" value={tLoading?'…':openTickets}
          sub={highPriority>0?`${highPriority} high priority`:'All normal priority'}
          subColor={highPriority>0?'#FF3B30':'#34C759'} trendUp={highPriority===0}
          spark={[5,4,6,3,openTickets]} sparkColor="#FF9F0A"
          icon={<AlertTriangle size={15} color="#FF9F0A"/>} iconBg="rgba(255,159,10,.1)"
          delay=".1s"
        />
        <KPICard
          title="Avg Resolution" value={avgResHrs!=null?`${avgResHrs}h`:(tLoading?'…':'—')}
          sub={avgResHrs!=null?(avgResHrs<=24?'Within SLA':'SLA breach risk'):'No data yet'}
          subColor={avgResHrs!=null&&avgResHrs<=24?'#34C759':'#FF9F0A'} trendUp={avgResHrs!=null&&avgResHrs<=24}
          icon={<Clock size={15} color="#AF52DE"/>} iconBg="rgba(175,82,222,.1)"
          delay=".15s"
        />
      </div>

      {/* ── Row 2: Revenue trend + Ticket summary ── */}
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14,marginBottom:14}}>

        {/* Revenue trend */}
        <div className="a-card" style={{background:'#fff',borderRadius:18,padding:'18px 20px',
          boxShadow:'0 2px 12px rgba(0,0,0,.06)',border:'1px solid rgba(0,0,0,.06)',
          animation:'afadeUp .4s ease .2s both'}}>
          <div style={{fontSize:12,fontWeight:700,color:'#1c1c1e',marginBottom:14}}>📈 Revenue Trend</div>
          {/* Mini bar chart */}
          <div style={{display:'flex',alignItems:'flex-end',gap:5,height:70,marginBottom:10}}>
            {MONTHLY_REVENUE.map(m => {
              const maxR = Math.max(...MONTHLY_REVENUE.map(x=>x.rev));
              const pct  = (m.rev/maxR)*100;
              const isLatest = m.month === latestRev.month;
              return (
                <div key={m.month} style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',gap:3,height:'100%',justifyContent:'flex-end'}}>
                  <div style={{
                    width:'100%',borderRadius:'4px 4px 0 0',
                    background: isLatest ? 'linear-gradient(180deg,#34C759,#248A3D)' : 'linear-gradient(180deg,#007AFF55,#007AFF33)',
                    height:`${Math.max(6,pct)}%`,transition:'height .5s ease',
                  }}/>
                  <div style={{fontSize:8,color:isLatest?'#34C759':'#8E8E93',fontWeight:isLatest?700:500}}>{m.month}</div>
                </div>
              );
            })}
          </div>
          {/* Last 4 months table */}
          <div style={{borderTop:'1px solid rgba(0,0,0,.05)',paddingTop:10}}>
            {MONTHLY_REVENUE.slice(-4).map(m => (
              <div key={m.month} style={{display:'flex',justifyContent:'space-between',alignItems:'center',
                padding:'5px 0',borderBottom:'1px solid rgba(0,0,0,.03)'}}>
                <span style={{fontSize:12,color:'#8E8E93',fontWeight:500,minWidth:28}}>{m.month}</span>
                <span style={{fontSize:13,fontWeight:800,color:'#1c1c1e'}}>₹{m.rev.toLocaleString()}</span>
                <span style={{fontSize:11,color:'#007AFF',fontWeight:600}}>{m.rc} recharges</span>
                <span style={{fontSize:10,color:'#8E8E93'}}>₹{Math.round(m.rev/m.rc).toLocaleString()}/avg</span>
              </div>
            ))}
          </div>
          <div style={{marginTop:10,display:'flex',justifyContent:'space-between',
            padding:'8px 12px',background:'rgba(52,199,89,.05)',borderRadius:10,border:'1px solid rgba(52,199,89,.15)'}}>
            <span style={{fontSize:12,color:'#248A3D',fontWeight:600}}>Total Revenue (7 months)</span>
            <span style={{fontSize:13,fontWeight:800,color:'#248A3D'}}>₹{MONTHLY_REVENUE.reduce((s,m)=>s+m.rev,0).toLocaleString()}</span>
          </div>
        </div>

        {/* Ticket analytics */}
        <div className="a-card" style={{background:'#fff',borderRadius:18,padding:'18px 20px',
          boxShadow:'0 2px 12px rgba(0,0,0,.06)',border:'1px solid rgba(0,0,0,.06)',
          animation:'afadeUp .4s ease .25s both'}}>
          <div style={{fontSize:12,fontWeight:700,color:'#1c1c1e',marginBottom:14}}>🎫 Support Ticket Summary</div>

          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:14}}>
            {[
              { label:'Open',     value:tLoading?'…':openTickets,  color:'#FF9F0A', bg:'rgba(255,159,10,.08)'  },
              { label:'Closed',   value:tLoading?'…':closedTickets, color:'#34C759', bg:'rgba(52,199,89,.08)'   },
              { label:'Total',    value:tLoading?'…':tickets.length,color:'#007AFF', bg:'rgba(0,122,255,.08)'   },
              { label:'High Pri', value:tLoading?'…':highPriority,  color:'#FF3B30', bg:'rgba(255,59,48,.08)'   },
            ].map(s => (
              <div key={s.label} style={{padding:'12px 14px',borderRadius:12,background:s.bg}}>
                <div style={{fontSize:22,fontWeight:800,color:s.color}}>{s.value}</div>
                <div style={{fontSize:11,color:'#8E8E93',marginTop:2}}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* Resolution time breakdown */}
          {closedWithTime.length > 0 && (
            <div style={{borderTop:'1px solid rgba(0,0,0,.05)',paddingTop:12}}>
              <div style={{fontSize:11,fontWeight:600,color:'#8E8E93',marginBottom:8}}>RESOLUTION TIME BREAKDOWN</div>
              {[
                { label:'≤ 24h (Fast)',   count: closedWithTime.filter(t=>((new Date(t.end_time||t.resolved_at||t.closed_at)-new Date(t.created_at))/36e5)<=24).length, color:'#34C759' },
                { label:'24–72h (Normal)',count: closedWithTime.filter(t=>{const h=(new Date(t.end_time||t.resolved_at||t.closed_at)-new Date(t.created_at))/36e5;return h>24&&h<=72;}).length, color:'#FF9F0A' },
                { label:'> 72h (Slow)',   count: closedWithTime.filter(t=>((new Date(t.end_time||t.resolved_at||t.closed_at)-new Date(t.created_at))/36e5)>72).length, color:'#FF3B30' },
              ].map(r => (
                <div key={r.label} style={{marginBottom:8}}>
                  <div style={{display:'flex',justifyContent:'space-between',marginBottom:4}}>
                    <span style={{fontSize:11,color:'#48484a'}}>{r.label}</span>
                    <span style={{fontSize:11,fontWeight:700,color:r.color}}>{r.count}</span>
                  </div>
                  <div style={{background:'rgba(0,0,0,.05)',borderRadius:999,height:5}}>
                    <div style={{height:'100%',borderRadius:999,background:r.color,
                      width:`${closedWithTime.length?((r.count/closedWithTime.length)*100):0}%`,transition:'width .6s ease'}}/>
                  </div>
                </div>
              ))}
            </div>
          )}
          {closedWithTime.length === 0 && (
            <div style={{textAlign:'center',padding:'16px 0',color:'#8E8E93',fontSize:12}}>
              Resolution time data will appear once tickets are closed
            </div>
          )}
        </div>
      </div>

      {/* ── Row 3: Plan distribution + Device by location ── */}
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14,marginBottom:14}}>

        {/* Plan distribution */}
        <div className="a-card" style={{background:'#fff',borderRadius:18,padding:'18px 20px',
          boxShadow:'0 2px 12px rgba(0,0,0,.06)',border:'1px solid rgba(0,0,0,.06)',
          animation:'afadeUp .4s ease .3s both'}}>
          <div style={{fontSize:12,fontWeight:700,color:'#1c1c1e',marginBottom:4}}>💳 Plan Distribution</div>
          <div style={{fontSize:11,color:'#8E8E93',marginBottom:14}}>{totalPlans} total active plans</div>
          {PLANS.map(p => (
            <div key={p.name} style={{marginBottom:12}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:5}}>
                <div style={{display:'flex',alignItems:'center',gap:7}}>
                  <div style={{width:8,height:8,borderRadius:'50%',background:p.color,flexShrink:0}}/>
                  <span style={{fontSize:12,color:'#48484a',fontWeight:600}}>{p.name}</span>
                </div>
                <div style={{display:'flex',gap:12,alignItems:'center'}}>
                  <span style={{fontSize:12,fontWeight:800,color:'#1c1c1e'}}>{p.count}</span>
                  <span style={{fontSize:11,color:'#8E8E93'}}>₹{p.revenue.toLocaleString()}</span>
                </div>
              </div>
              <div style={{background:'rgba(0,0,0,.05)',borderRadius:999,height:7,overflow:'hidden'}}>
                <div style={{height:'100%',borderRadius:999,background:`linear-gradient(90deg,${p.color},${p.color}99)`,
                  width:`${(p.count/maxPlan)*100}%`,transition:'width .7s ease',
                  boxShadow:`0 0 8px ${p.color}40`}}/>
              </div>
            </div>
          ))}
          <div style={{marginTop:14,paddingTop:12,borderTop:'1px solid rgba(0,0,0,.05)',
            display:'flex',justifyContent:'space-between',alignItems:'center',
            background:'rgba(0,122,255,.04)',padding:'10px 12px',borderRadius:10,
            border:'1px solid rgba(0,122,255,.1)'}}>
            <div>
              <div style={{fontSize:11,color:'#8E8E93'}}>Total Plan Revenue</div>
              <div style={{fontSize:18,fontWeight:800,color:'#007AFF'}}>₹{totalRev.toLocaleString()}</div>
            </div>
            <div style={{textAlign:'right'}}>
              <div style={{fontSize:11,color:'#8E8E93'}}>Avg per plan</div>
              <div style={{fontSize:15,fontWeight:700,color:'#1c1c1e'}}>₹{totalPlans?Math.round(totalRev/totalPlans).toLocaleString():'—'}</div>
            </div>
          </div>
        </div>

        {/* Devices by location — real data */}
        <div className="a-card" style={{background:'#fff',borderRadius:18,padding:'18px 20px',
          boxShadow:'0 2px 12px rgba(0,0,0,.06)',border:'1px solid rgba(0,0,0,.06)',
          animation:'afadeUp .4s ease .35s both'}}>
          <div style={{fontSize:12,fontWeight:700,color:'#1c1c1e',marginBottom:4}}>📍 Devices by Location</div>
          <div style={{fontSize:11,color:'#8E8E93',marginBottom:14}}>
            {loading?'Loading…':`${total} total registered devices`}
          </div>
          {loading ? (
            <div style={{textAlign:'center',padding:'24px 0',color:'#8E8E93',fontSize:12}}>Loading…</div>
          ) : topLocations.length === 0 ? (
            <div style={{textAlign:'center',padding:'24px 0',color:'#8E8E93',fontSize:12}}>No location data available</div>
          ) : topLocations.map(([loc, count], i) => (
            <div key={loc} style={{marginBottom:12}}>
              <div style={{display:'flex',justifyContent:'space-between',marginBottom:5}}>
                <span style={{fontSize:12,color:'#48484a',fontWeight:600}}>{loc}</span>
                <span style={{fontSize:12,fontWeight:800,color:'#1c1c1e'}}>{count}</span>
              </div>
              <div style={{background:'rgba(0,0,0,.05)',borderRadius:999,height:7,overflow:'hidden'}}>
                <div style={{height:'100%',borderRadius:999,
                  background:`linear-gradient(90deg,${LOC_COLORS[i%LOC_COLORS.length]},${LOC_COLORS[i%LOC_COLORS.length]}99)`,
                  width:`${(count/maxLoc)*100}%`,transition:'width .7s ease',
                  boxShadow:`0 0 8px ${LOC_COLORS[i%LOC_COLORS.length]}40`}}/>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Row 4: Device model breakdown + Product types ── */}
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14,marginBottom:28}}>

        {/* Model breakdown */}
        <div className="a-card" style={{background:'#fff',borderRadius:18,padding:'18px 20px',
          boxShadow:'0 2px 12px rgba(0,0,0,.06)',border:'1px solid rgba(0,0,0,.06)',
          animation:'afadeUp .4s ease .4s both'}}>
          <div style={{fontSize:12,fontWeight:700,color:'#1c1c1e',marginBottom:14}}>🔩 Device Models</div>
          {loading ? (
            <div style={{textAlign:'center',padding:'24px 0',color:'#8E8E93',fontSize:12}}>Loading…</div>
          ) : topModels.length === 0 ? (
            <div style={{textAlign:'center',padding:'24px 0',color:'#8E8E93',fontSize:12}}>No data</div>
          ) : topModels.map(([model, count], i) => {
            const pct = (count / total) * 100;
            return (
              <div key={model} style={{display:'flex',alignItems:'center',gap:12,
                padding:'10px 12px',borderRadius:12,background:'#fafafa',
                border:'1px solid rgba(0,0,0,.05)',marginBottom:8}}>
                <div style={{width:34,height:34,borderRadius:10,flexShrink:0,
                  background:LOC_COLORS[i%LOC_COLORS.length]+'18',
                  display:'flex',alignItems:'center',justifyContent:'center',
                  fontSize:14,fontWeight:800,color:LOC_COLORS[i%LOC_COLORS.length]}}>
                  {count}
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:11,fontWeight:700,color:'#1c1c1e',
                    overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{model}</div>
                  <div style={{marginTop:4,background:'rgba(0,0,0,.07)',borderRadius:999,height:4}}>
                    <div style={{height:'100%',borderRadius:999,
                      background:LOC_COLORS[i%LOC_COLORS.length],width:`${pct}%`,transition:'width .6s'}}/>
                  </div>
                </div>
                <span style={{fontSize:10,color:'#8E8E93',fontWeight:600,flexShrink:0}}>{Math.round(pct)}%</span>
              </div>
            );
          })}
        </div>

        {/* Business health summary */}
        <div className="a-card" style={{background:'#fff',borderRadius:18,padding:'18px 20px',
          boxShadow:'0 2px 12px rgba(0,0,0,.06)',border:'1px solid rgba(0,0,0,.06)',
          animation:'afadeUp .4s ease .45s both'}}>
          <div style={{fontSize:12,fontWeight:700,color:'#1c1c1e',marginBottom:14}}>✅ Business Health</div>
          {[
            {
              label: 'Device Coverage',
              value: total > 0 ? 'Active' : 'No devices',
              icon: total > 0 ? <CheckCircle size={16} color="#34C759"/> : <AlertTriangle size={16} color="#FF9F0A"/>,
              detail: `${total} registered device${total!==1?'s':''}`,
              color: total > 0 ? '#34C759' : '#FF9F0A',
            },
            {
              label: 'Ticket Resolution',
              value: avgResHrs!=null ? (avgResHrs<=24?'Fast':'Needs attention') : 'No data',
              icon: avgResHrs!=null&&avgResHrs<=24 ? <CheckCircle size={16} color="#34C759"/> : <Clock size={16} color="#FF9F0A"/>,
              detail: avgResHrs!=null ? `Avg ${avgResHrs}h per ticket` : 'Close tickets to see data',
              color: avgResHrs!=null&&avgResHrs<=24 ? '#34C759' : '#FF9F0A',
            },
            {
              label: 'Open Tickets',
              value: openTickets === 0 ? 'All clear' : `${openTickets} pending`,
              icon: openTickets===0 ? <CheckCircle size={16} color="#34C759"/> : <AlertTriangle size={16} color={highPriority>0?'#FF3B30':'#FF9F0A'}/>,
              detail: highPriority > 0 ? `${highPriority} high priority require urgent action` : 'No high priority tickets',
              color: openTickets===0 ? '#34C759' : highPriority>0 ? '#FF3B30' : '#FF9F0A',
            },
            {
              label: 'Monthly Revenue',
              value: revGrowth >= 0 ? '📈 Growing' : '📉 Declining',
              icon: revGrowth >= 0 ? <ArrowUp size={16} color="#34C759"/> : <ArrowDown size={16} color="#FF3B30"/>,
              detail: `${revGrowth>=0?'+':''}${revGrowth}% vs previous month`,
              color: revGrowth >= 0 ? '#34C759' : '#FF3B30',
            },
            {
              label: 'Plan Sales',
              value: `${totalPlans} active plans`,
              icon: <CheckCircle size={16} color="#007AFF"/>,
              detail: `₹${totalRev.toLocaleString()} total revenue`,
              color: '#007AFF',
            },
          ].map(item => (
            <div key={item.label} style={{display:'flex',alignItems:'center',gap:12,
              padding:'10px 12px',borderRadius:12,background:'#fafafa',
              border:'1px solid rgba(0,0,0,.05)',marginBottom:8}}>
              {item.icon}
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:12,fontWeight:700,color:'#1c1c1e'}}>{item.label}</div>
                <div style={{fontSize:11,color:'#8E8E93',marginTop:1}}>{item.detail}</div>
              </div>
              <span style={{fontSize:11,fontWeight:700,color:item.color,flexShrink:0,textAlign:'right'}}>
                {item.value}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
